import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Search, Filter, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { ResponseModeBadge } from '../components/dashboard/StatusBadge';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Logs = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(20);

  // Fetch requests from API
  const fetchRequests = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (currentPage * limit).toString(),
      });

      if (filterMode !== 'all') {
        params.append('response_mode', filterMode);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/api/analytics/requests?${params}`);
      const data = await response.json();

      setRequests(data.requests || []);
      setTotal(data.total || 0);
    } catch (error) {
      // Error logging - production should use proper error service
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching requests:', error);
      }
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterMode, searchTerm, limit]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleExport = () => {
    toast.success('Exporting logs to CSV...');
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Request Logs</h1>
          <p className="text-muted-foreground mt-1">
            Detailed request history and audit trail
          </p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by trace ID or destination..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Response Mode</label>
              <Select value={filterMode} onValueChange={setFilterMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modes</SelectItem>
                  <SelectItem value="live">Live Rates</SelectItem>
                  <SelectItem value="zone">Zone Fallback</SelectItem>
                  <SelectItem value="estimate">Country Estimate</SelectItem>
                  <SelectItem value="error">Errors</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Request History
          </CardTitle>
          <CardDescription>
            Showing {requests.length} of {total} requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trace ID</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Response Mode</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Latency</TableHead>
                      <TableHead>Cache</TableHead>
                      <TableHead>Weight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.trace_id} className="hover:bg-accent/50 transition-colors">
                        <TableCell className="font-mono text-xs">
                          {req.trace_id.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {format(new Date(req.timestamp), 'MMM d, HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="text-sm font-medium">{req.destination.city}</div>
                            <div className="text-xs text-muted-foreground">{req.destination.country}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={req.status_code === 200 ? "outline" : "destructive"}>
                            {req.response_mode}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{req.provider_used || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <span className={req.latency_ms > 2000 ? 'text-destructive' : req.latency_ms > 1000 ? 'text-warning' : 'text-success'}>
                            {req.latency_ms}ms
                          </span>
                        </TableCell>
                        <TableCell>
                          {req.cache_hit ? (
                            <Badge variant="outline" className="bg-success-light text-success border-success/20 text-xs">
                              Hit
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-muted text-muted-foreground text-xs">
                              Miss
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {req.total_weight_kg ? `${req.total_weight_kg.toFixed(2)} kg` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {totalPages || 1}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage >= totalPages - 1 || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Logs;
